/**
 * The Finish Line's words (#1714): pure helpers the job page's Next block,
 * meter and promise list read. No React here, so each rule has its own test.
 * The engine decides every state; this file only says it.
 */
import { PHASES, type FinishLine, type JobPromise, type Owner, type Phase } from "@/lib/finish-line";

export const PHASE_LABEL: Record<Phase, string> = {
    before_visit: "Before the visit",
    on_site: "On site",
    after_visit: "After the visit",
    money: "Money",
};

const OWNER_WORD: Record<Owner, string> = {
    office: "the office",
    tech: "the tech",
    customer: "the customer",
    city: "the city",
    vendor: "the vendor",
};

/** "the customer" / "the city": who someone is waiting on, in words. */
export function ownerWords(owner: Owner): string {
    return OWNER_WORD[owner];
}

/**
 * Pre-visit: nobody has been to the site yet. The prototype calls the meter
 * "Pre-flight" here, because everything on it is preparation.
 */
export function isPreVisit(job: {
    status: string;
    arrived_at?: string | null;
    work_started_at?: string | null;
    completed_at?: string | null;
}): boolean {
    if (["draft", "awaiting_scheduling", "scheduled", "unqueued"].includes(job.status)) return true;
    if (job.status === "held") return !job.arrived_at && !job.work_started_at && !job.completed_at;
    return false;
}

/** "3 days", "5 hours", "just now": how long something has been owed. */
export function ageWords(since: string | undefined, now: Date = new Date()): string | null {
    if (!since) return null;
    const t = Date.parse(since);
    if (Number.isNaN(t)) return null;
    const mins = Math.max(0, Math.round((now.getTime() - t) / 60_000));
    if (mins < 60) return "just now";
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? "" : "s"}`;
}

/** The right-hand label on a list row. */
export function stateLabel(p: JobPromise, now: Date = new Date()): string {
    const age = ageWords(p.since, now);
    switch (p.state) {
        case "kept":
            return "Kept";
        case "open":
            return age ? `On us · ${age}` : "On us";
        case "missed":
            return "Missed";
        case "blocked":
            return "Blocked";
        case "later":
            return "After the visit";
        case "waiting":
            return `Waiting on ${p.owner === "office" ? "us" : p.owner}${age ? ` · ${age}` : ""}`;
        default:
            return "";
    }
}

export const STATE_GLYPH: Record<JobPromise["state"], string> = {
    kept: "✓",
    open: "○",
    missed: "○",
    blocked: "✕",
    waiting: "◷",
    not_applicable: "·",
    later: "·",
};

/** The truth sentences that fired on one promise, in the engine's order. */
export function truthSentences(fl: FinishLine, p: JobPromise): string[] {
    if (!p.truthIds?.length) return [];
    return fl.truths.filter((t) => p.truthIds!.includes(t.id)).map((t) => t.sentence);
}

/** The first unkept promise this one waits on ("Blocked by: …"). */
export function blockedByTitle(fl: FinishLine, p: JobPromise): string | null {
    if (p.state !== "blocked" || !p.blockedBy?.length) return null;
    for (const id of p.blockedBy) {
        const b = fl.promises.find((x) => x.id === id);
        if (b && b.state !== "kept" && b.state !== "not_applicable") return b.title;
    }
    return null;
}

export interface PhaseGroup {
    phase: Phase;
    label: string;
    promises: JobPromise[];
}

/** The full list, grouped by phase in the engine's order. "Doesn't apply"
 *  rows are left out, and so is a phase with nothing left in it. */
export function groupByPhase(fl: FinishLine): PhaseGroup[] {
    return PHASES.map((phase) => ({
        phase,
        label: PHASE_LABEL[phase],
        promises: fl.promises.filter((p) => p.phase === phase && p.state !== "not_applicable"),
    })).filter((g) => g.promises.length > 0);
}

/** "Before the visit 5/7 · On site 4/6 …": kept / applicable, per phase. */
export function phaseTallies(fl: FinishLine): { phase: Phase; label: string; kept: number; total: number }[] {
    return PHASES.map((phase) => ({
        phase,
        label: PHASE_LABEL[phase],
        kept: fl.counts.byPhase[phase]?.kept ?? 0,
        total: fl.counts.byPhase[phase]?.total ?? 0,
    })).filter((t) => t.total > 0);
}

/** Bar segment widths in percent (kept, on us, blocked, waiting). */
export function meterWidths(fl: FinishLine): { kept: number; onUs: number; blocked: number; waiting: number } {
    const c = fl.counts;
    const pct = (n: number) => (c.total > 0 ? Math.round((1000 * n) / c.total) / 10 : 0);
    return { kept: pct(c.kept), onUs: pct(c.onUs), blocked: pct(c.blocked), waiting: pct(c.waiting) };
}

/**
 * When there's no next move for the office: who the job is waiting on.
 * Order: someone outside (waiting) → an open promise nobody here can press →
 * anything still blocked. All kept → null (the page says so).
 */
export function waitingHeadline(fl: FinishLine): { who: string; promise: JobPromise } | null {
    const live = fl.promises.filter((p) => p.state !== "not_applicable" && p.state !== "kept" && p.state !== "later");
    const pick =
        live.find((p) => p.state === "waiting") ??
        live.find((p) => p.state === "open" || p.state === "missed") ??
        live.find((p) => p.state === "blocked");
    if (!pick) return null;
    return { who: pick.owner === "office" ? "us" : ownerWords(pick.owner), promise: pick };
}

/**
 * Where an action's link goes. A link back to THIS job's page (the engine
 * writes `/jobs/<id>#devices`, `#paperwork`, `#contacts`, or the bare page
 * for booking moves) stays on the page and opens the right section; anything
 * else is a route.
 */
export type LinkTarget =
    | { kind: "section"; section: "devices" | "paperwork" | "contacts" | "schedule" }
    | { kind: "route"; to: string };

export function linkTarget(href: string, jobId: string): LinkTarget {
    const base = `/jobs/${jobId}`;
    if (href === base) return { kind: "section", section: "schedule" };
    if (href.startsWith(`${base}#`)) {
        const hash = href.slice(base.length + 1);
        if (hash === "devices" || hash === "paperwork" || hash === "contacts")
            return { kind: "section", section: hash };
        return { kind: "section", section: "schedule" };
    }
    return { kind: "route", to: href };
}
